import { ActionIcon, Badge, Card, Checkbox, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconBriefcase, IconCalendar, IconPencil, IconStar, IconStarFilled, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

function dueMeta(task) {
  if (!task.dueDate) return null;
  // DueDate is a date-only value anchored to UTC midnight on the backend, and "today" is UTC there
  // too (My Day, counts, reminders). Compare both as UTC calendar days so the badge, the My Day
  // membership and the backend agree in every timezone.
  const due = new Date(task.dueDate);
  const dueKey = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const now = new Date();
  const todayKey = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return {
    date: due,
    overdue: !task.isCompleted && dueKey < todayKey,
    isToday: !task.isCompleted && dueKey === todayKey,
  };
}

export default function TaskList({ tasks, onToggleComplete, onToggleImportant, onEdit, onDelete, emptyMessage, hideProjectTag = false }) {
  const { t, i18n } = useTranslation("Veft");

  if (!tasks.length) return <Text c="dimmed" ta="center" py="xl">{emptyMessage}</Text>;

  return (
    <Stack gap="xs">
      {tasks.map((task) => {
        const due = dueMeta(task);
        // The project link gets its own heavier badge (it's the primary grouping); the candidate and
        // client links stay as the lighter dot badges. On a project's own task panel the project badge
        // would be redundant, so it's suppressed there (hideProjectTag).
        const showProject = task.projectTitle && !hideProjectTag;
        const otherLinks = [task.candidateName, task.clientName].filter(Boolean);
        return (
          <Card key={task.id} withBorder p="sm" radius="md">
            <Group wrap="nowrap" align="flex-start">
              <Checkbox
                checked={task.isCompleted}
                onChange={() => onToggleComplete(task)}
                aria-label={t("tasks_list_completed")}
                mt={2}
              />
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Text
                  fw={500}
                  td={task.isCompleted ? "line-through" : undefined}
                  c={task.isCompleted ? "dimmed" : undefined}
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {task.title}
                </Text>
                {task.description && <Text size="xs" c="dimmed" lineClamp={2}>{task.description}</Text>}
                {(due || showProject || otherLinks.length > 0 || task.assigneeName) && (
                  <Group gap="xs" wrap="wrap">
                    {due && (
                      <Badge
                        size="xs"
                        variant="light"
                        color={due.overdue ? "red" : due.isToday ? "orange" : "gray"}
                        leftSection={<IconCalendar size={10} />}
                      >
                        {due.overdue ? `${t("tasks_overdue")} · ` : ""}
                        {due.date.toLocaleDateString(i18n.language, { timeZone: "UTC" })}
                      </Badge>
                    )}
                    {showProject && (
                      <Badge size="sm" variant="light" color="indigo" leftSection={<IconBriefcase size={11} />}>
                        {task.projectTitle}
                      </Badge>
                    )}
                    {otherLinks.map((l, i) => (
                      <Badge key={i} size="xs" variant="dot" color="teal">{l}</Badge>
                    ))}
                    {task.assigneeName && <Text size="xs" c="dimmed">· {task.assigneeName}</Text>}
                  </Group>
                )}
              </Stack>
              <Group gap={2} wrap="nowrap">
                <Tooltip label={t("tasks_important")}>
                  <ActionIcon
                    variant="subtle"
                    color={task.isImportant ? "yellow" : "gray"}
                    onClick={() => onToggleImportant(task)}
                    aria-label={t("tasks_important")}
                  >
                    {task.isImportant ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t("edit", "Edit")}>
                  <ActionIcon variant="subtle" onClick={() => onEdit(task)} aria-label={t("edit", "Edit")}>
                    <IconPencil size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t("delete", "Delete")}>
                  <ActionIcon variant="subtle" color="red" onClick={() => onDelete(task)} aria-label={t("delete", "Delete")}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}
